import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthFlowFrame from "../../components/AuthFlowFrame";
import SidePanel from "../../components/SidePanel";
import { showError } from "../../helpers";
import { saveAngiAccountNumber } from "../../services";
import { Content } from "@jobber/components/Content";
import { Divider } from "@jobber/components/Divider";
import { Heading } from "@jobber/components/Heading";
import { Icon } from "@jobber/components/Icon";
import { InputText } from "@jobber/components/InputText";
import { Link } from "@jobber/components/Link";
import { Text } from "@jobber/components/Text";
import { Typography } from "@jobber/components/Typography";
import { Form } from "@jobber/components/Form";
import { useFormState } from "@jobber/hooks";
import accountNumberImage from "../../assets/images/angi_account_number.png";

function Step1() {
    const [{ isDirty, isValid }, setFormState] = useFormState();
    const [accountNumber, setAccountNumber] = useState("");

    const [isSavingAccountNumber, setIsSavingAccountNumber] = useState(false);
    const [buttonText, setButtonText] = useState("Connect Accounts");
    const navigate = useNavigate();

    function saveAccountNumber(e: React.MouseEvent) {
        e.preventDefault();
        setIsSavingAccountNumber(true);
        setButtonText("Connecting...");
        (async () => {
            try {
                const state = sessionStorage.getItem('state');
                await saveAngiAccountNumber(parseInt(accountNumber), state);
                navigate("/jobber-angi-step2", {replace: true});
            } catch(e: any) {
                showError("Error connecting account: " + e.message);
            }
        })();
    }

    return (
        <AuthFlowFrame
            pretitle={"Let's get you set up"}
            title={"Connect to Angi Leads"}
            sidePanel={
                <SidePanel>
                    <Heading level={2}>
                        How to find your <Typography emphasisType={"highlight"}>Angi Leads Account Number</Typography>
                    </Heading>
                    <ol style={{
                        paddingInlineStart: "20px",
                        lineHeight: "24px",
                    }}>
                        <li>Open your Angi Leads account</li>
                        <li>Select <b>My business</b> from the top menu</li>
                        <li>Select the <b>Statements</b> subtab</li>
                        <li>Under <b>Account Overview</b>, copy your <b>Account Number</b></li>
                    </ol>
                    <img src={accountNumberImage} alt={"Sync complete"} style={{maxWidth: "100%"}}></img>
                </SidePanel>
            }
            sidePanelContained={false}
            step={1}
            totalSteps={2}
            button={{
                link: "/jobber-angi-step2",
                text: buttonText,
                type: "primary",
                disabled: !isDirty || !isValid,
                loading: isSavingAccountNumber,
                onClick: saveAccountNumber,
            }}
        >
            <Content type={"div"} spacing={"base"}>
                <Text size={"large"}>
                    Start syncing Angi Leads with Jobber by entering your Angi Leads Account Number below.
                </Text>

                <Text size={"base"} variation={"subdued"}>
                    Please note: it can take up to 1 business day for Angi Leads to finalize the integration setup.
                </Text>

                <div
                    style={{
                        maxWidth: "350px",
                    }}
                >
                    <Form
                        onStateChange={setFormState}
                    >
                        <InputText
                            name={"accountNumber"}
                            placeholder={"Enter Angi Leads Account Number"}
                            keyboard={"numeric"}
                            validations={{
                                required: {
                                    value: true,
                                    message: "Enter a valid Account Number.",
                                },
                                pattern: {
                                    value: /^\d+$/,
                                    message: "Account Number must not contain letters or symbols.",
                                },
                            }}
                            onChange={(accountNumber) => setAccountNumber(accountNumber as string)}
                        />
                    </Form>
                </div>

                <Text size={"base"}>
                    <Icon name={"export"} color={"green"}/>&nbsp;
                    <Link url={"https://office.angi.com/"} external={true}>Go to Angi Leads</Link>
                </Text>

                <Divider direction={"horizontal"} />

                <Text size={"base"} variation={"subdued"}>
                    Don’t have an Angi Leads account? <Link url={"https://homeadvisor.com/jobber"} external={true}>Create
                    an Angi Leads account</Link> and then come back to this tab to finish setting up your integration.
                </Text>
            </Content>
        </AuthFlowFrame>
    );
}

export default Step1;
